import React, { useEffect } from 'react';
import { responseInterface } from 'swr';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import MeetingDetails from '../../../app/shared/MeetingDetails/MeetingDetails';
import EventDetails from '../../../app/shared/EventDetails';
import { HTTP_STATUS, ResponseError } from '../../../config/api';
import { useMeetingPrivateDetails } from '../../../hooks/api/protected/use-meeting-private-details';
import { useMeetingId } from '../../../hooks/use-meeting-id';
import { useEventId } from '../../../hooks/use-event-id';
import { PrivateMeetingDetailsResponseType } from '../../../lib/api';
import { EventSettingsContextType, useEventSettings } from '../../../lib/context-providers/event-settings-context/event-settings-context';
import useConnectMeetingDisabled from '../../../hooks/use-connect-meeting-disabled';
import useDefaultRoutePage from '../../../hooks/use-default-route-page';
import {
  BOOK_A_MEETING_SOURCE,
  findBookAMeetingPathForMeetingId,
  resolveBookAMeetingOriginPath
} from '../../../lib/helpers/bookAMeetingNavigationHelper';
import { usePathPrefix } from '../../../hooks/use-path-prefix';
import { IAuthContext, useAuth } from '../../../lib/context-providers/auth-context';
import MeetingNotAvailableMessage from '../../shared/MeetingDetails/MeetingNotAvailableMessage';

import './MeetingWrapper.scss';

const MeetingWrapper: React.FC = () => {
  const meetingId = useMeetingId();
  const eventId = useEventId();
  const eventSettings = useEventSettings() as EventSettingsContextType;
  const landingPage = useDefaultRoutePage();
  const history = useHistory();
  const location = useLocation();
  const pathPrefix = usePathPrefix();
  const auth = useAuth() as IAuthContext;
  const { data: meetingDetails, error } = useMeetingPrivateDetails(eventId, meetingId) as responseInterface<PrivateMeetingDetailsResponseType, ResponseError>;
  const isConnectBtnDisabled = useConnectMeetingDisabled(eventSettings, meetingDetails?.startDateTime);

  // Calendar/deep links: resolve the correct Book a Meeting page (supports multiple pages + sourcePath query param)
  useEffect(() => {
    if (location.state || !eventSettings) {
      return;
    }

    let cancelled = false;

    const applyBookAMeetingOrigin = async (): Promise<void> => {
      const searchParams = new URLSearchParams(location.search);
      let originPath = resolveBookAMeetingOriginPath({
        eventSettings,
        pathPrefix,
        isAuthenticated: auth.isAuthenticated,
        searchParams
      });

      const isBookAMeetingSource = searchParams.get('source') === BOOK_A_MEETING_SOURCE;
      const shouldLookupMeetingPage =
        !originPath &&
        Boolean(eventId && meetingId) &&
        (isBookAMeetingSource || meetingDetails?.access === 'BOOKABLE');

      if (shouldLookupMeetingPage) {
        originPath = await findBookAMeetingPathForMeetingId(
          eventId,
          meetingId,
          eventSettings,
          pathPrefix,
          auth.isAuthenticated
        );
      }

      if (!cancelled && originPath) {
        history.replace(location.pathname + location.search, originPath);
      }
    };

    void applyBookAMeetingOrigin();

    return (): void => {
      cancelled = true;
    };
  }, [
    location.state,
    location.search,
    location.pathname,
    eventSettings,
    pathPrefix,
    auth.isAuthenticated,
    history,
    eventId,
    meetingId,
    meetingDetails?.access
  ]);

  if (error && (error.status === HTTP_STATUS.FORBIDDEN || error.status === HTTP_STATUS.NOT_FOUND)) {
    return <Redirect to={landingPage} />;
  }

  if (meetingDetails && meetingDetails.isAvailable === false) {
    return (
      <MeetingNotAvailableMessage
        showTopNavBtns={true}
        agendaLabel={eventSettings.agendaLabel}
      />
    );
  }

  if (eventSettings && meetingDetails) {
    return (
      <>
        <EventDetails details={eventSettings}/>
        <MeetingDetails
          meetingDetails={meetingDetails}
          showTopNavBtns={true}
          timeZone={eventSettings.timeZone}
          agendaLabel={eventSettings.agendaLabel}
          isConnectBtnDisabled={isConnectBtnDisabled}
          eventName={eventSettings.name}
          eventId={eventId}
          showAddToCalendarBtn={true}
          eventCustomTitle={eventSettings.eventTitleForPassport}
          className='min-h-24rem'
          meetingLinksLabel={eventSettings.meetingLinksLabel}
          calendarInvite={eventSettings.calendarInvite}
        />
      </>
    );
  }
  return null;
};

export default MeetingWrapper;
